import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Instagram, Twitter, Mail } from 'lucide-react';
import pb from '@/lib/pocketbaseClient.js';
import { DEFAULT_SITE_SETTINGS, normalizeSiteSettings, visibleSocialPlatforms } from '@/lib/siteSettings.js';

const TikTokIcon = ({ size = 20 }) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 18a4 4 0 1 1-4-4" /><path d="M9 18V3h4a5 5 0 0 0 5 5" /><path d="M18 8v4" /></svg>;

const SocialIcon = ({ platform }) => {
  if (platform === 'linkedin') return <Linkedin size={20} />;
  if (platform === 'tiktok') return <TikTokIcon />;
  if (platform === 'instagram') return <Instagram size={20} />;
  return <Twitter size={20} />;
};

const candidateLinks = [
  ['Browse jobs', '/jobs'],
  ['Career Finder', '/career-finder'],
  ['Early Careers', '/early-careers'],
  ['Talent Network', '/talent-pool'],
  ['Job alerts', '/job-alerts'],
];

const companyLinks = [
  ['Blog', '/insights'],
  ['Partners', '/partners'],
  ['About us', '/about'],
  ['Contact', '/contact'],
];

const FooterLinks = ({ title, links }) => <div><span className="mb-4 block text-sm font-semibold tracking-wide">{title}</span><ul className="space-y-2">{links.map(([label, path]) => <li key={path}><Link to={path} className="text-sm text-secondary-foreground/80 transition-colors duration-200 hover:text-primary">{label}</Link></li>)}</ul></div>;

const Footer = () => {
  const [siteSettings, setSiteSettings] = useState(DEFAULT_SITE_SETTINGS);
  const openCookieSettings = () => window.dispatchEvent(new Event('luxe:open-cookie-settings'));

  useEffect(() => {
    pb.collection('SiteSettings').getFirstListItem('key="jobs_page"', { $autoCancel: false })
      .then(record => setSiteSettings(normalizeSiteSettings(record)))
      .catch(() => setSiteSettings(DEFAULT_SITE_SETTINGS));
  }, []);

  const socialLinks = useMemo(() => visibleSocialPlatforms(siteSettings), [siteSettings]);

  return <footer className="mt-20 bg-secondary text-secondary-foreground">
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-[1.2fr_0.8fr_0.8fr_1.1fr]">
        <div>
          <div className="mb-4 text-brand-logo">Luxe Jobs <span className="text-brand-accent">London</span></div>
          <p className="max-w-xs text-sm leading-relaxed text-secondary-foreground/80">Curated careers across luxury retail, beauty, hospitality and supporting professional teams.</p>
        </div>

        <FooterLinks title="For candidates" links={candidateLinks} />
        <FooterLinks title="Luxe Jobs London" links={companyLinks} />

        <div>
          <span className="mb-4 block text-sm font-semibold tracking-wide">Get in touch</span>
          <div className="mb-6 flex items-center space-x-2">
            <Mail size={18} className="text-primary" />
            <a href="mailto:contact@luxejobslondon.co.uk" className="text-sm font-medium text-secondary-foreground/80 transition-colors duration-200 hover:text-primary">contact@luxejobslondon.co.uk</a>
          </div>
          <span className="mb-3 block text-sm font-semibold tracking-wide">Follow us</span>
          <div className="flex min-h-5 space-x-4">
            {socialLinks.map(platform => <a key={platform.key} href={platform.url} target="_blank" rel="noopener noreferrer" className="text-secondary-foreground/80 transition-colors duration-200 hover:text-primary" aria-label={`Follow Luxe Jobs London on ${platform.label}`}><SocialIcon platform={platform.key} /></a>)}
          </div>
        </div>
      </div>

      <div className="mt-8 flex flex-col items-center justify-between border-t border-secondary-foreground/20 pt-6 text-sm text-secondary-foreground/60 sm:flex-row">
        <p>&copy; 2026 Luxe Jobs London. All rights reserved.</p>
        <div className="mt-4 flex flex-wrap justify-center gap-4 sm:mt-0 sm:space-x-6">
          <Link to="/privacy" className="transition-colors duration-200 hover:text-primary">Privacy & cookies</Link>
          <button type="button" onClick={openCookieSettings} className="transition-colors duration-200 hover:text-primary">Cookie settings</button>
          <Link to="/auth" className="ml-auto text-secondary-foreground/40 transition-colors duration-200 hover:text-primary sm:ml-0" aria-label="Admin Login">Login</Link>
        </div>
      </div>
    </div>
  </footer>;
};

export default Footer;
